/**
 * Client Admin console — tenant-scoped Dashboard / Users / Groups / Devices / Invites / Policies / Settings.
 * Platform admins default to Nutrinium via X-Tenant-Id from /tenant/me.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Users,
  FolderTree,
  Laptop,
  Link2,
  Shield,
  Settings,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import BulkEnrollmentPanel from './BulkEnrollmentPanel';
import { HubPage, HubPanel, HubTabs } from './ui/HubShell';

type Tab = 'dashboard' | 'users' | 'groups' | 'devices' | 'invites' | 'policies' | 'compliance' | 'reports' | 'settings' | 'rbac';

export default function ClientAdminConsole() {
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [me, setMe] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [policies, setPolicies] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', full_name: '', role: 'CLIENT_VIEWER' });
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState({ name: '', group_type: 'department' });
  const [compliancePack, setCompliancePack] = useState<any>({ rules: [], thresholds: {}, notes: '' });
  const [complianceDraft, setComplianceDraft] = useState({ rules_json: '[]', thresholds_json: '{}', notes: '' });

  const loadMe = useCallback(async () => {
    try {
      const data = await apiService.tenantMe();
      setMe(data);
      return data;
    } catch (e: any) {
      setError(e?.message || 'Failed to load tenant context');
      return null;
    }
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    const ctx = await loadMe();
    if (!ctx) return;
    try {
      const [u, g, d, p, pack] = await Promise.all([
        apiService.tenantListUsers(),
        apiService.tenantListGroups(),
        apiService.tenantListDevices(),
        apiService.tenantListPolicies(),
        apiService.tenantGetCompliancePack(),
      ]);
      setUsers(Array.isArray(u) ? u : []);
      setGroups(Array.isArray(g) ? g : []);
      setDevices(Array.isArray(d) ? d : []);
      setPolicies(Array.isArray(p) ? p : []);
      setCompliancePack(pack || { rules: [], thresholds: {}, notes: '' });
      setComplianceDraft({
        rules_json: JSON.stringify(pack?.rules || [], null, 2),
        thresholds_json: JSON.stringify(pack?.thresholds || {}, null, 2),
        notes: pack?.notes || '',
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load tenant data');
    }
  }, [loadMe]);

  useEffect(() => {
    reload();
  }, [reload]);

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={14} /> },
    { id: 'users', label: 'Users', icon: <Users size={14} /> },
    { id: 'groups', label: 'Groups', icon: <FolderTree size={14} /> },
    { id: 'devices', label: 'Devices', icon: <Laptop size={14} /> },
    { id: 'invites', label: 'Enrollment Invites', icon: <Link2 size={14} /> },
    { id: 'policies', label: 'Policies', icon: <Shield size={14} /> },
    { id: 'compliance', label: 'Compliance', icon: <Shield size={14} /> },
    { id: 'reports', label: 'Reports', icon: <LayoutDashboard size={14} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={14} /> },
    { id: 'rbac', label: 'RBAC / Access', icon: <Shield size={14} /> },
  ];

  const enabledModules: string[] = Array.isArray(me?.modules) ? me.modules : [];
  const visibleTabs = tabs.filter((t) => {
    if (['dashboard', 'settings', 'users', 'groups', 'rbac'].includes(t.id)) return true;
    if (t.id === 'devices' || t.id === 'invites') return enabledModules.length === 0 || enabledModules.includes('devices') || enabledModules.includes('enrollment');
    if (t.id === 'policies') return enabledModules.length === 0 || enabledModules.includes('policies');
    if (t.id === 'compliance') return enabledModules.length === 0 || enabledModules.includes('compliance');
    if (t.id === 'reports') return enabledModules.length === 0 || enabledModules.includes('analytics');
    return true;
  });

  const byOs = devices.reduce<Record<string, number>>((acc, d) => {
    const k = (d.os || 'unknown').toLowerCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <HubPage
      title="Client Admin"
      subtitle={`${me?.tenant?.name || 'Tenant'} (${me?.tenant?.code || '—'}) · signed in as ${user?.username || '—'}${me?.is_platform_admin ? ' · Platform Admin (Nutrinium scope)' : ''}`}
      icon={<LayoutDashboard size={20} />}
      eyebrow="Tenant console"
      action={
        <button type="button" onClick={reload} className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2">{error}</div>}

      <HubPanel withTabs padded={false}>
        <HubTabs
          variant="segment"
          value={tab}
          onChange={setTab}
          items={visibleTabs.map((t) => ({ id: t.id, label: t.label, icon: t.icon }))}
        />
        <div className="hub-panel__inner min-h-[320px]">
        {tab === 'dashboard' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-slate-500">Devices</p><p className="text-2xl font-bold">{devices.length}</p></div>
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-slate-500">Users</p><p className="text-2xl font-bold">{users.length}</p></div>
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-slate-500">Groups</p><p className="text-2xl font-bold">{groups.length}</p></div>
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-slate-500">Policies</p><p className="text-2xl font-bold">{policies.length}</p></div>
            <div className="md:col-span-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Devices by OS</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(byOs).map(([os, n]) => (
                  <span key={os} className="px-2 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-semibold">{os}: {n}</span>
                ))}
                {devices.length === 0 && <span className="text-sm text-slate-500">No devices yet.</span>}
              </div>
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
              <input
                className="border rounded-lg px-2 py-1.5 text-sm"
                placeholder="Username"
                value={newUser.username}
                disabled={!!editingUserId}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              />
              <input
                className="border rounded-lg px-2 py-1.5 text-sm"
                type="password"
                placeholder={editingUserId ? 'New password (optional)' : 'Password'}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
              <input
                className="border rounded-lg px-2 py-1.5 text-sm"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
              <input
                className="border rounded-lg px-2 py-1.5 text-sm"
                placeholder="Full name"
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
              />
              <select className="border rounded-lg px-2 py-1.5 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="CLIENT_ADMIN">CLIENT_ADMIN</option>
                <option value="CLIENT_MANAGER">CLIENT_MANAGER</option>
                <option value="CLIENT_VIEWER">CLIENT_VIEWER</option>
                <option value="DEVICE_USER">DEVICE_USER</option>
              </select>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm inline-flex items-center justify-center gap-1"
                  onClick={async () => {
                    try {
                      if (editingUserId) {
                        const body: Record<string, unknown> = {
                          role: newUser.role,
                          email: newUser.email || null,
                          full_name: newUser.full_name || null,
                        };
                        if (newUser.password) body.password = newUser.password;
                        await apiService.tenantUpdateUser(editingUserId, body);
                        toast.success('User updated');
                      } else {
                        await apiService.tenantCreateUser(newUser);
                        toast.success('User added');
                      }
                      setNewUser({ username: '', password: '', email: '', full_name: '', role: 'CLIENT_VIEWER' });
                      setEditingUserId(null);
                      reload();
                    } catch (e: any) {
                      toast.error(e?.message || (editingUserId ? 'Failed to update user' : 'Failed to add user'));
                    }
                  }}
                >
                  <Plus size={14} /> {editingUserId ? 'Save' : 'Add user'}
                </button>
                {editingUserId && (
                  <button
                    type="button"
                    className="px-3 py-1.5 border rounded-lg text-sm"
                    onClick={() => {
                      setEditingUserId(null);
                      setNewUser({ username: '', password: '', email: '', full_name: '', role: 'CLIENT_VIEWER' });
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2">Username</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Default policy</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-50">
                    <td className="py-2 font-medium">{u.username}</td>
                    <td>{u.role}</td>
                    <td>{u.email || '—'}</td>
                    <td>{u.is_active === false ? 'Disabled' : 'Active'}</td>
                    <td>
                      <select
                        className="border rounded px-1 py-0.5 text-xs max-w-[160px]"
                        value={u.default_policy_id || ''}
                        onChange={async (e) => {
                          try {
                            await apiService.tenantSetUserPolicy(u.id, e.target.value || null);
                            reload();
                            toast.success('User policy updated');
                          } catch (err: any) {
                            toast.error(err?.message || 'Failed to update user policy');
                          }
                        }}
                      >
                        <option value="">—</option>
                        {policies.map((p) => (
                          <option key={p.policy_id} value={p.policy_id}>{p.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-indigo-600"
                        onClick={() => {
                          setEditingUserId(u.id);
                          setNewUser({
                            username: u.username || '',
                            password: '',
                            email: u.email || '',
                            full_name: u.full_name || '',
                            role: u.role || 'CLIENT_VIEWER',
                          });
                        }}
                      >
                        Edit
                      </button>
                      {u.is_active === false ? (
                        <button
                          type="button"
                          className="text-emerald-600"
                          onClick={async () => {
                            try {
                              await apiService.tenantUpdateUser(u.id, { is_active: true });
                              reload();
                              toast.success('User enabled');
                            } catch (e: any) {
                              toast.error(e?.message || 'Failed to enable user');
                            }
                          }}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-red-600"
                          onClick={async () => {
                            if (!window.confirm(`Disable user ${u.username}?`)) return;
                            try {
                              await apiService.tenantDeleteUser(u.id);
                              reload();
                              toast.success('User disabled');
                            } catch (e: any) {
                              toast.error(e?.message || 'Failed to disable user');
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'groups' && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <input className="border rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[140px]" placeholder="Name" value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} />
              <select className="border rounded-lg px-2 py-1.5 text-sm" value={newGroup.group_type} onChange={(e) => setNewGroup({ ...newGroup, group_type: e.target.value })}>
                <option value="group">group</option>
                <option value="department">department</option>
                <option value="location">location</option>
              </select>
              <button
                type="button"
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.tenantCreateGroup(newGroup);
                    setNewGroup({ name: '', group_type: 'department' });
                    reload();
                    toast.success('Group added');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to add group');
                  }
                }}
              >
                Add
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Assign a default policy per group — used by enrollment cascade (invite → user → group → tenant default).
            </p>
            <ul className="divide-y">
              {groups.map((g) => (
                <li key={g.id} className="py-2 text-sm flex flex-wrap items-center gap-2 justify-between">
                  <span>
                    <span className="font-medium">{g.name}</span>
                    <span className="text-slate-500 ml-2">{g.group_type}</span>
                  </span>
                  <select
                    className="border rounded-lg px-2 py-1 text-xs max-w-[220px]"
                    value={g.default_policy_id || ''}
                    onChange={async (e) => {
                      const val = e.target.value || null;
                      try {
                        await apiService.tenantSetGroupPolicy(g.id, val);
                        reload();
                        toast.success('Group policy updated');
                      } catch (err: any) {
                        toast.error(err?.message || 'Failed to update group policy');
                      }
                    }}
                  >
                    <option value="">No default policy</option>
                    {policies.map((p) => (
                      <option key={p.policy_id} value={p.policy_id}>{p.name}</option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === 'devices' && (
          <div className="overflow-auto max-h-[480px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2">Name</th><th>OS</th><th>Mode</th><th>Status</th><th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.device_id} className="border-b border-slate-50">
                    <td className="py-2 font-medium">{d.name}</td>
                    <td>{d.os}</td>
                    <td className="text-xs">{d.management_mode || '—'}</td>
                    <td>{d.status}</td>
                    <td className="text-xs">{d.last_seen ? new Date(d.last_seen).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'invites' && (
          <BulkEnrollmentPanel
            groups={groups.map((g) => ({ id: String(g.id), name: String(g.name || g.id) }))}
          />
        )}

        {tab === 'policies' && (
          <ul className="divide-y text-sm">
            {policies.map((p) => (
              <li key={p.policy_id} className="py-2 flex justify-between">
                <span className="font-medium">{p.name}</span>
                <span className="text-slate-500">{p.platform} · v{p.version}</span>
              </li>
            ))}
            {policies.length === 0 && <li className="text-slate-500 py-4">No policies yet.</li>}
          </ul>
        )}

        {tab === 'compliance' && (
          <div className="text-sm space-y-4">
            <p className="text-slate-600">
              Tenant device compliance snapshot from the scoped device registry.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['compliant', 'non_compliant', 'unknown', 'active'].map((k) => {
                const count = devices.filter((d) =>
                  k === 'active' ? d.status === 'active' : (d.compliance_status || 'unknown') === k
                ).length;
                return (
                  <div key={k} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">{k.replace('_', ' ')}</p>
                    <p className="text-xl font-bold">{count}</p>
                  </div>
                );
              })}
            </div>
            <div className="border-t pt-4 space-y-2">
              <p className="font-medium">Tenant compliance pack</p>
              <p className="text-xs text-slate-500">
                Store tenant-scoped rules/thresholds (JSON). Hub → Compliance remains for global rule engine.
              </p>
              <label className="block text-xs">
                Rules (JSON array)
                <textarea
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 font-mono text-xs"
                  rows={4}
                  value={complianceDraft.rules_json}
                  onChange={(e) => setComplianceDraft({ ...complianceDraft, rules_json: e.target.value })}
                />
              </label>
              <label className="block text-xs">
                Thresholds (JSON)
                <textarea
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 font-mono text-xs"
                  rows={3}
                  value={complianceDraft.thresholds_json}
                  onChange={(e) => setComplianceDraft({ ...complianceDraft, thresholds_json: e.target.value })}
                />
              </label>
              <label className="block text-xs">
                Notes
                <input
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm"
                  value={complianceDraft.notes}
                  onChange={(e) => setComplianceDraft({ ...complianceDraft, notes: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.tenantPutCompliancePack({
                      rules: JSON.parse(complianceDraft.rules_json || '[]'),
                      thresholds: JSON.parse(complianceDraft.thresholds_json || '{}'),
                      notes: complianceDraft.notes || null,
                    });
                    reload();
                    toast.success('Compliance pack saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save compliance pack');
                  }
                }}
              >
                Save compliance pack
              </button>
              {Array.isArray(compliancePack?.rules) && (
                <p className="text-xs text-slate-400">{compliancePack.rules.length} rule(s) stored</p>
              )}
            </div>
          </div>
        )}

        {tab === 'reports' && (
          <div className="text-sm space-y-3">
            <p className="text-slate-600">Quick inventory report for this tenant.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2">OS</th><th>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byOs).map(([os, n]) => (
                  <tr key={os} className="border-b border-slate-50">
                    <td className="py-2 font-medium">{os}</td>
                    <td>{n}</td>
                  </tr>
                ))}
                {Object.keys(byOs).length === 0 && (
                  <tr><td colSpan={2} className="py-4 text-slate-500">No devices.</td></tr>
                )}
              </tbody>
            </table>
            <p className="text-xs text-slate-400">Advanced analytics: Hub → Analytics.</p>
          </div>
        )}

        {tab === 'settings' && (
          <ClientPackageSettings me={me} onRefresh={async () => {
            try {
              const m = await apiService.tenantMe();
              setMe(m);
            } catch { /* ignore */ }
          }} />
        )}

        {tab === 'rbac' && <ClientRbacPanel users={users} />}
        </div>
      </HubPanel>
    </HubPage>
  );
}

function ClientPackageSettings({ me, onRefresh }: { me: any; onRefresh: () => Promise<void> }) {
  const toast = useToast();
  const [pkg, setPkg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    source_mode: 'uem_hosted',
    client_share_base: '',
    client_share_type: 'unc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiService.getTenantPackageSettings();
      setPkg(s);
      setDraft({
        source_mode: s?.source_mode || 'uem_hosted',
        client_share_base: s?.client_share_base || '',
        client_share_type: s?.client_share_type || (String(s?.client_share_base || '').startsWith('http') ? 'http' : 'unc'),
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load package settings');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="text-sm space-y-4 max-w-xl">
      <div className="space-y-2">
        <p><span className="text-slate-500">Tenant:</span> <strong>{me?.tenant?.name}</strong> ({me?.tenant?.code})</p>
        <p><span className="text-slate-500">Theme:</span> {me?.branding?.theme_color || '—'}</p>
        <p><span className="text-slate-500">Enrollment message:</span> {me?.branding?.enrollment_message || '—'}</p>
        <p><span className="text-slate-500">Support:</span> {me?.branding?.support_contact || '—'}</p>
        <p className="text-xs text-slate-400">Branding edits are available under Platform Admin → Clients → Branding.</p>
        <div>
          <p className="text-slate-500 mb-1">Enabled modules</p>
          <div className="flex flex-wrap gap-1">
            {(me?.modules || []).map((m: string) => (
              <span key={m} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{m}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4 space-y-3">
        <h3 className="font-semibold text-slate-800">Package repository</h3>
        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
              <p>
                <span className="text-slate-500">UEM path:</span>{' '}
                <span className="font-mono">{pkg?.uem_absolute_path || '—'}</span>
              </p>
              <p>
                <span className="text-slate-500">Quota:</span> {pkg?.used_gb ?? 0} / {pkg?.quota_gb ?? 0} GB used
              </p>
            </div>
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-slate-600 mb-1">Where installers live</legend>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="pkg_mode"
                  checked={draft.source_mode === 'uem_hosted'}
                  onChange={() => setDraft({ ...draft, source_mode: 'uem_hosted' })}
                />
                <span>
                  <strong>UEM hosted</strong>
                  <span className="block text-xs text-slate-500">Upload packages into the allocated UEM folder above.</span>
                </span>
              </label>
              <label className={`flex items-start gap-2 ${pkg?.allow_client_share === false ? 'opacity-50' : ''}`}>
                <input
                  type="radio"
                  name="pkg_mode"
                  disabled={pkg?.allow_client_share === false}
                  checked={draft.source_mode === 'client_share'}
                  onChange={() => setDraft({ ...draft, source_mode: 'client_share' })}
                />
                <span>
                  <strong>Own share</strong>
                  <span className="block text-xs text-slate-500">
                    Point to your UNC or HTTPS package share. Platform Admin must allow this.
                  </span>
                </span>
              </label>
            </fieldset>
            {draft.source_mode === 'client_share' && (
              <div className="space-y-2 pl-1">
                <label className="block text-xs font-medium text-slate-600">
                  Share type
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    value={draft.client_share_type}
                    onChange={(e) => setDraft({ ...draft, client_share_type: e.target.value })}
                  >
                    <option value="unc">UNC (\\\\server\\share)</option>
                    <option value="http">HTTPS / HTTP URL</option>
                  </select>
                </label>
                <label className="block text-xs font-medium text-slate-600">
                  Share base path / URL
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    placeholder={draft.client_share_type === 'http' ? 'https://files.example.com/packages' : '\\\\fileserver\\packages'}
                    value={draft.client_share_base}
                    onChange={(e) => setDraft({ ...draft, client_share_base: e.target.value })}
                  />
                </label>
              </div>
            )}
            <button
              type="button"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm disabled:opacity-50"
              onClick={async () => {
                setSaving(true);
                try {
                  await apiService.updateTenantPackageSettings({
                    source_mode: draft.source_mode,
                    client_share_base: draft.client_share_base || null,
                    client_share_type: draft.client_share_type,
                  });
                  await load();
                  await onRefresh();
                  toast.success('Package settings saved');
                } catch (e: any) {
                  toast.error(e?.message || 'Failed to save');
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save package settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ClientRbacPanel({ users }: { users: any[] }) {
  const toast = useToast();
  const [roles, setRoles] = useState<any[]>([]);
  const [assignable, setAssignable] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [editKeys, setEditKeys] = useState<string[]>([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignRoleIds, setAssignRoleIds] = useState<string[]>([]);

  const reload = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        apiService.tenantRbacListRoles(),
        apiService.tenantRbacAssignable(),
      ]);
      setRoles(Array.isArray(r) ? r : []);
      setAssignable(Array.isArray(a) ? a : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load RBAC');
    }
  }, [toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="space-y-6 text-sm">
      <p className="text-slate-600">
        Create roles and assign permissions <strong>only within</strong> what Platform Admin enabled for this tenant.
        Dangerous permissions show a badge. Disabled entitlements never appear here.
      </p>

      <div className="flex flex-wrap gap-2 items-end">
        <input
          className="border rounded-lg px-2 py-1.5"
          placeholder="New role name"
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
        />
        <button
          type="button"
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg"
          onClick={async () => {
            try {
              await apiService.tenantRbacCreateRole({
                name: newRoleName,
                permission_keys: [],
                template: 'Read Only Viewer',
              });
              setNewRoleName('');
              reload();
              toast.success('Role created');
            } catch (e: any) {
              toast.error(e?.message || 'Create role failed');
            }
          }}
        >
          Create from Read Only template
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ul className="divide-y border rounded-lg">
          {roles.map((r) => (
            <li key={r.id} className="p-3 flex justify-between items-center gap-2">
              <button type="button" className="text-left" onClick={() => { setSelectedRole(r); setEditKeys(r.permission_keys || []); }}>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-slate-500">{r.permission_count} permissions {r.is_system_role ? '· system' : ''}</div>
              </button>
              {!r.is_system_role && (
                <button
                  type="button"
                  className="text-xs text-red-600"
                  onClick={async () => {
                    try {
                      await apiService.tenantRbacDeleteRole(r.id);
                      if (selectedRole?.id === r.id) setSelectedRole(null);
                      reload();
                      toast.success('Role deleted');
                    } catch (e: any) {
                      toast.error(e?.message || 'Delete failed');
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>

        <div className="border rounded-lg p-3 max-h-[420px] overflow-auto">
          {!selectedRole ? (
            <p className="text-slate-500">Select a role to edit permissions.</p>
          ) : (
            <>
              <p className="font-semibold mb-2">{selectedRole.name}</p>
              <div className="space-y-1 mb-3">
                {assignable.map((p) => (
                  <label key={p.permission_key} className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={editKeys.includes(p.permission_key)}
                      onChange={(e) => {
                        setEditKeys((prev) =>
                          e.target.checked
                            ? [...prev, p.permission_key]
                            : prev.filter((k) => k !== p.permission_key)
                        );
                      }}
                    />
                    <span>
                      <span className="font-mono">{p.permission_key}</span>
                      {p.is_dangerous && (
                        <span className="ml-1 px-1 rounded bg-red-100 text-red-700">dangerous</span>
                      )}
                      <div className="text-slate-500">{p.description}</div>
                    </span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg"
                onClick={async () => {
                  try {
                    await apiService.tenantRbacUpdateRole(selectedRole.id, { permission_keys: editKeys });
                    reload();
                    toast.success('Role permissions saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Update failed');
                  }
                }}
              >
                Save role permissions
              </button>
            </>
          )}
        </div>
      </div>

      <div className="border-t pt-4 space-y-2">
        <p className="font-medium">Assign roles to user</p>
        <div className="flex flex-wrap gap-2">
          <select className="border rounded-lg px-2 py-1.5" value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}>
            <option value="">Select user</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
          <select
            className="border rounded-lg px-2 py-1.5 min-w-[180px]"
            multiple
            value={assignRoleIds}
            onChange={(e) =>
              setAssignRoleIds(Array.from(e.target.selectedOptions).map((o) => o.value))
            }
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg"
            onClick={async () => {
              if (!assignUserId) {
                toast.info('Select a user first');
                return;
              }
              try {
                await apiService.tenantRbacSetUserRoles(assignUserId, assignRoleIds);
                toast.success('User roles saved');
              } catch (e: any) {
                toast.error(e?.message || 'Assign failed');
              }
            }}
          >
            Save user roles
          </button>
        </div>
        <p className="text-xs text-slate-400">Hold Ctrl/Cmd to multi-select roles.</p>
      </div>
    </div>
  );
}
